import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SetupCards } from '../components/setup/SetupCards';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  UsersRound, ClipboardCheck, MessageCircleQuestion, Lock, CalendarClock,
  ExternalLink, ArrowRight, Loader2,
} from 'lucide-react';

import {
  calendarApi,
  calendarConflictsApi,
  dailyPlanApi,
  decisionsApi,
  workItemsApi,
} from '../api/services';
import type {
  CalendarConflict,
  CalendarEvent,
  DailyPlan,
  Decision,
  WorkItem,
} from '../types/models';

import { usePrincipal, type Principal } from '../context/PrincipalContext';
import { useEntitlements } from '../context/EntitlementsContext';
import { eaLandingCopy } from '../copy/eaLanding';
import {
  deriveAwaitingApproval,
  deriveFocusDefenceAlerts,
  deriveFollowUpsDueToday,
  deriveNextMeetings,
  type AwaitingApprovalRow,
  type FocusDefenceRow,
  type FollowUpRow,
  type NextMeetingRow,
} from '../utils/eaLanding';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// EaLandingPage — what an Executive Assistant sees at `/`.
// Cross-principal signal in four cards. Fan-out happens per principal; the
// derivation helpers in utils/eaLanding return ready-to-render rows.
// ---------------------------------------------------------------------------

interface PrincipalFanoutState {
  principal: Principal;
  plan: DailyPlan | null;
  pendingDecisions: Decision[];
  workItems: WorkItem[];
  conflicts: CalendarConflict[];
  events: CalendarEvent[];
}

export default function EaLandingPage() {
  const navigate = useNavigate();
  const { principals, loading: principalsLoading, setActivePrincipal } = usePrincipal();
  // The decision queue is a leadership tool: without it there is nothing to
  // ask for (the server would refuse every principal's request).
  const decisionsIncluded = useEntitlements().has('decisions');

  const today = format(new Date(), 'yyyy-MM-dd');
  const prettyDate = format(new Date(), 'EEEE d MMMM');

  const [fanouts, setFanouts] = useState<PrincipalFanoutState[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(
    async (list: Principal[]) => {
      if (list.length === 0) {
        setFanouts([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const results = await Promise.all(
          list.map(async (principal) => {
            const [plan, pendingDecisions, workItems, conflicts, events] = await Promise.all([
              dailyPlanApi.getByDate(today, principal.id).catch((e: any) => {
                if (e?.response?.status === 404) return null;
                throw e;
              }),
              decisionsIncluded
                ? decisionsApi.getPending(principal.id).catch(() => [] as Decision[])
                : Promise.resolve([] as Decision[]),
              workItemsApi.getByDate(today, principal.id).catch(() => [] as WorkItem[]),
              calendarConflictsApi.getByDate(today, principal.id).catch(() => [] as CalendarConflict[]),
              calendarApi.getByDate(today, principal.id).catch(() => [] as CalendarEvent[]),
            ]);
            return { principal, plan, pendingDecisions, workItems, conflicts, events };
          }),
        );
        setFanouts(results);
      } catch (e: any) {
        setError(e?.response?.data?.error ?? 'Unable to load today’s workload.');
      } finally {
        setLoading(false);
      }
    },
    [today, decisionsIncluded],
  );

  useEffect(() => {
    if (principalsLoading) return;
    loadAll(principals);
  }, [principals, principalsLoading, loadAll]);

  const awaiting = useMemo<AwaitingApprovalRow[]>(() => deriveAwaitingApproval(fanouts), [fanouts]);
  const followUps = useMemo<FollowUpRow[]>(() => deriveFollowUpsDueToday(fanouts), [fanouts]);
  const focusDefence = useMemo<FocusDefenceRow[]>(() => deriveFocusDefenceAlerts(fanouts), [fanouts]);
  const nextMeetings = useMemo<NextMeetingRow[]>(() => deriveNextMeetings(fanouts), [fanouts]);

  const principalById = useMemo(() => {
    const map = new Map<string, Principal>();
    for (const p of principals) map.set(p.id, p);
    return map;
  }, [principals]);

  const openForPrincipal = useCallback(
    (ref: { id: string; name: string }, path: string) => {
      const full = principalById.get(ref.id);
      if (full) setActivePrincipal(full);
      navigate(path);
    },
    [navigate, principalById, setActivePrincipal],
  );

  return (
    <div className="mx-auto max-w-[1200px] p-4 md:p-6">
      {/* Header */}
      <div className="mb-1 flex items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <UsersRound className="h-7 w-7 text-primary" />
            <h1 className="font-serif text-2xl font-light tracking-tight">{eaLandingCopy.pageHeading}</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {prettyDate}. {eaLandingCopy.pageSubheading}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/ea-workspace')}
          className="gap-1.5"
        >
          {eaLandingCopy.viewEaWorkspace}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Guided setup, part two: the Claude checklist or the assistant card (or nothing). */}
      <SetupCards />

      {error && (
        <Alert variant="error" className="mt-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {(loading || principalsLoading) && fanouts.length === 0 && (
        <div className="py-12 text-center">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      )}

      {!principalsLoading && principals.length === 0 && (
        <Card className="mt-6">
          <CardContent className="py-10 text-center">
            <p className="mb-2 text-base font-semibold">No principals assigned yet</p>
            <p className="text-sm text-muted-foreground">
              Ask your tenant admin to assign you to the executives you support. Once you are assigned,
              this page will show their plans, follow-ups, focus conflicts, and meetings.
            </p>
          </CardContent>
        </Card>
      )}

      {fanouts.length > 0 && (
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <SectionCard
            heading={eaLandingCopy.awaitingApprovalHeading}
            icon={<ClipboardCheck className="h-5 w-5 text-primary" />}
            countBadge={awaiting.length}
          >
            {awaiting.length === 0 ? (
              <EmptyRow text={eaLandingCopy.awaitingApprovalEmpty} />
            ) : (
              <div className="flex flex-col gap-1.5">
                {awaiting.map((row, i) => (
                  <AwaitingApprovalItem
                    key={`${row.principal.id}-${row.kind}-${row.decisionId ?? i}`}
                    row={row}
                    onOpen={() => openForPrincipal(row.principal, row.kind === 'plan' ? '/plan' : '/work-items')}
                  />
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard
            heading={eaLandingCopy.followUpsHeading}
            icon={<MessageCircleQuestion className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
            countBadge={followUps.length}
          >
            {followUps.length === 0 ? (
              <EmptyRow text={eaLandingCopy.followUpsEmpty} />
            ) : (
              <div className="flex flex-col gap-1.5">
                {followUps.map((row) => (
                  <FollowUpItem
                    key={row.workItem.id}
                    row={row}
                    onOpen={() => openForPrincipal(row.principal, '/work-items')}
                  />
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard
            heading={eaLandingCopy.focusDefenceHeading}
            icon={<Lock className="h-5 w-5 text-rose-600 dark:text-rose-400" />}
            countBadge={focusDefence.length}
          >
            {focusDefence.length === 0 ? (
              <EmptyRow text={eaLandingCopy.focusDefenceEmpty} />
            ) : (
              <div className="flex flex-col gap-1.5">
                {focusDefence.map((row, i) => (
                  <FocusDefenceItem
                    key={`${row.principal.id}-${i}`}
                    row={row}
                    onOpen={() => openForPrincipal(row.principal, '/focus')}
                  />
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard
            heading={eaLandingCopy.briefingPackHeading}
            icon={<CalendarClock className="h-5 w-5 text-primary" />}
            countBadge={nextMeetings.filter((r) => r.event).length}
          >
            {nextMeetings.every((r) => r.event === null) ? (
              <EmptyRow text={eaLandingCopy.briefingPackEmpty} />
            ) : (
              <div className="flex flex-col gap-1.5">
                {nextMeetings.map((row) => (
                  <NextMeetingItem
                    key={row.principal.id}
                    row={row}
                    onOpen={() => openForPrincipal(row.principal, '/calendar')}
                  />
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section card shell + helpers
// ---------------------------------------------------------------------------

function SectionCard({
  heading,
  icon,
  countBadge,
  children,
}: {
  heading: string;
  icon: React.ReactNode;
  countBadge?: number;
  children: React.ReactNode;
}) {
  return (
    <Card className="h-full">
      <CardContent>
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {icon}
            <p className="text-base font-semibold">{heading}</p>
          </div>
          {typeof countBadge === 'number' && countBadge > 0 && (
            <span className="inline-flex items-center rounded-full border border-primary/60 bg-transparent px-2 py-0.5 text-[11px] font-semibold text-primary">
              {countBadge}
            </span>
          )}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <p className="py-1 text-sm text-muted-foreground">{text}</p>;
}

function PrincipalBadge({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">
      {name}
    </span>
  );
}

function RowShell({
  children,
  onOpen,
  className,
}: {
  children: React.ReactNode;
  onOpen?: () => void;
  className?: string;
}) {
  if (!onOpen) {
    return (
      <div className={cn('rounded-2xl border border-border p-3', className)}>
        {children}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'block w-full rounded-2xl border border-border p-3 text-left transition-colors hover:border-primary',
        className,
      )}
    >
      {children}
    </button>
  );
}

function AwaitingApprovalItem({ row, onOpen }: { row: AwaitingApprovalRow; onOpen: () => void }) {
  return (
    <RowShell onOpen={onOpen}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{row.title}</p>
          <div className="mt-1 flex items-center gap-2">
            <PrincipalBadge name={row.principal.name} />
            <p className="text-xs text-muted-foreground">
              {row.kind === 'plan'
                ? eaLandingCopy.awaitingApprovalPlan
                : eaLandingCopy.awaitingApprovalDecision}
            </p>
          </div>
        </div>
        <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
    </RowShell>
  );
}

function FollowUpItem({ row, onOpen }: { row: FollowUpRow; onOpen: () => void }) {
  return (
    <RowShell onOpen={onOpen}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{row.workItem.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <PrincipalBadge name={row.principal.name} />
            {row.chasing && (
              <p className="text-xs text-muted-foreground">
                {eaLandingCopy.followUpChasing.replace('{name}', row.chasing)}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {eaLandingCopy.followUpSince.replace('{date}', row.since.slice(0, 10))}
            </p>
          </div>
        </div>
        <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
    </RowShell>
  );
}

function FocusDefenceItem({ row, onOpen }: { row: FocusDefenceRow; onOpen: () => void }) {
  const { conflict } = row;
  const isHard = conflict.severity === 'Hard';
  const severityLabel = isHard
    ? eaLandingCopy.focusDefenceHard
    : eaLandingCopy.focusDefenceSoft;
  const severityClass = isHard
    ? 'border-rose-500/60 text-rose-600 dark:text-rose-400'
    : 'border-amber-500/60 text-amber-700 dark:text-amber-400';
  const timeLabel = `${format(new Date(conflict.startTime), 'HH:mm')} to ${format(new Date(conflict.endTime), 'HH:mm')}`;

  return (
    <RowShell onOpen={onOpen}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{conflict.description}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <PrincipalBadge name={row.principal.name} />
            <span
              className={cn(
                'inline-flex items-center rounded-full border bg-transparent px-2 py-0.5 text-[10px] font-semibold',
                severityClass,
              )}
            >
              {severityLabel}
            </span>
            <p className="text-xs text-muted-foreground">{timeLabel}</p>
          </div>
        </div>
        <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
    </RowShell>
  );
}

function NextMeetingItem({ row, onOpen }: { row: NextMeetingRow; onOpen: () => void }) {
  if (!row.event) {
    return (
      <RowShell className="opacity-70">
        <div className="flex items-center gap-2">
          <PrincipalBadge name={row.principal.name} />
          <p className="text-xs text-muted-foreground">{eaLandingCopy.briefingPackNoUpcoming}</p>
        </div>
      </RowShell>
    );
  }

  const start = format(new Date(row.event.startTime), 'HH:mm');

  return (
    <RowShell onOpen={onOpen}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{start} · {row.event.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <PrincipalBadge name={row.principal.name} />
            {row.event.attendees && (
              <p className="text-xs text-muted-foreground">{row.event.attendees}</p>
            )}
          </div>
        </div>
        <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
    </RowShell>
  );
}
